import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PackageTab.css';

function PackageTab() {
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [fetchingDashboards, setFetchingDashboards] = useState(false);
  const [packaging, setPackaging] = useState(false);
  const [packageStatus, setPackageStatus] = useState('');
  const [packageData, setPackageData] = useState(null);
  const [packageFilename, setPackageFilename] = useState('');
  const [error, setError] = useState(null);
  const [pollIntervalId, setPollIntervalId] = useState(null);

  const fetchDashboards = async () => {
    setFetchingDashboards(true);
    setError(null);
    setDashboards([]);
    setSelectedDashboard('');
    setPackageData(null);
    setPackageFilename('');

    try {
      const response = await axios.get('/api/v1/dashboards/list');
      setDashboards(response.data.dashboards || []);
    } catch (err) {
      setError('Failed to retrieve dashboards. Please try again.');
      console.error('Error fetching dashboards:', err);
    } finally {
      setFetchingDashboards(false);
    }
  };

  const pollPackageStatus = async (jobId) => {
    try {
      const response = await axios.get(`/api/v1/dashboards/package/status/${jobId}`);
      const { status, package_data, filename, error: jobError } = response.data;

      if (status === 'completed') {
        setPackageData(package_data);
        setPackageFilename(filename);
        setPackaging(false);
        setPackageStatus('');
        return true;
      }

      if (status === 'failed') {
        setError(jobError || 'Packaging failed');
        setPackaging(false);
        setPackageStatus('');
        return true;
      }

      setPackageStatus(status);
      return false;
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to check packaging status');
      setPackaging(false);
      setPackageStatus('');
      return true;
    }
  };

  const generatePackage = async () => {
    if (!selectedDashboard) return;

    setPackaging(true);
    setPackageStatus('pending');
    setError(null);
    setPackageData(null);

    try {
      const response = await axios.post('/api/v1/dashboards/package', {
        dashboard_api_name: selectedDashboard
      });
      const jobId = response.data.job_id;
      console.log('Packaging job started:', jobId);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const interval = setInterval(async () => {
        const shouldStop = await pollPackageStatus(jobId);
        if (shouldStop) {
          clearInterval(interval);
          setPollIntervalId(null);
        }
      }, 2000);
      setPollIntervalId(interval);

      await pollPackageStatus(jobId);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to start packaging job');
      setPackaging(false);
      setPackageStatus('');
    }
  };

  const downloadPackage = () => {
    if (!packageData) return;

    const dataStr = JSON.stringify(packageData, null, 4);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = packageFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    return () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
      }
    };
  }, [pollIntervalId]);

  return (
    <div>
      <h2>Dashboard Packaging</h2>
      <div className="alert-info">
        💡 This process analyzes a dashboard, finds all its dependencies, and bundles them into a single `.json` file for easy migration.
      </div>

      <div className="container-bordered">
        <h3>1. Select Your Dashboard</h3>
        <button
          className="button-primary"
          onClick={fetchDashboards}
          disabled={fetchingDashboards || packaging}
        >
          {fetchingDashboards ? 'Fetching...' : 'Fetch Available Dashboards'}
        </button>
        <p className="help-text">Click to load dashboards from your Salesforce org</p>

        {dashboards.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <label htmlFor="dashboard-select" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Select a dashboard from your org...
            </label>
            <select
              id="dashboard-select"
              className="select-field"
              value={selectedDashboard}
              onChange={(e) => setSelectedDashboard(e.target.value)}
              disabled={packaging}
            >
              <option value="">-- Choose a dashboard --</option>
              {dashboards.map((dash) => (
                <option key={dash.name} value={dash.name}>
                  {dash.label} (API_NAME: {dash.name})
                </option>
              ))}
            </select>
            <p className="help-text">Choose the dashboard you want to package from this list.</p>
          </div>
        )}
      </div>

      {selectedDashboard && (
        <>
          <hr className="divider" />
          <div className="container-bordered">
            <h3>2. Create & Download Your Package</h3>
            <div className="alert-info">
              Ready to process <strong>{selectedDashboard}</strong> and all its dependencies.
            </div>
            <button
              className="button-primary"
              onClick={generatePackage}
              disabled={packaging}
            >
              {packaging ? 'Packaging...' : 'Generate Package'}
            </button>
          </div>
        </>
      )}

      {error && (
        <div className="alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {packaging && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>
            Packaging dashboard... this can take up to a minute.
            {packageStatus === 'running' && ' (working)'}
            {packageStatus === 'pending' && ' (queued)'}
          </p>
        </div>
      )}

      {packageData && (
        <>
          <hr className="divider" />
          <div className="container-bordered">
            <h2>Download Your Package</h2>
            <div className="alert-success">
              Package generated successfully!
            </div>
            <button className="button-primary" onClick={downloadPackage}>
              ⬇️ Download Package File (.json)
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default PackageTab;
